import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FilterSelect } from '@wundo/ui';
import { searchCohorts } from '@/lib/cohorts.ts';
import { cohortLabel } from './cohortLabel.ts';

const ALL = '';

export interface CohortFilterProps {
  /** Undefined is "all cohorts". */
  value: string | undefined;
  onChange: (value: string | undefined) => void;
  organisationId: string;
}

/**
 * Narrow a dashboard to one cohort of the organisation, as a page-header
 * filter. Newest first: the cohort somebody just made is the one they look for.
 */
export function CohortFilter({ value, onChange, organisationId }: CohortFilterProps) {
  const { data: cohorts, isPending } = useQuery({
    queryKey: ['cohorts', 'picker', organisationId],
    queryFn: () => searchCohorts({ query: '', organisationId }),
  });

  const options = useMemo(
    () => [
      { value: ALL, label: 'All cohorts' },
      ...[...(cohorts ?? [])]
        .sort(
          (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
        )
        .map((cohort) => ({
          value: cohort.id,
          label: cohortLabel(cohort, { withOrganisation: false }),
        })),
    ],
    [cohorts],
  );

  return (
    <FilterSelect
      hideLabel
      label='Cohort'
      className='max-w-80'
      options={options}
      value={value ?? ALL}
      placeholder={isPending ? 'Loading…' : 'All cohorts'}
      disabled={isPending}
      onValueChange={(next) => onChange(next === ALL ? undefined : next)}
    />
  );
}
